import { useEffect, useRef, useState, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CountryFlag } from "@/lib/identity";
import { defaultRoomFor } from "@/lib/regions";
import { PaperPlaneRight, PushPin as Pushpin, Trash, Circle, ShieldCheck, Globe } from "@phosphor-icons/react";

const MAX_LEN = 280;

const ROOMS = [
  { id: "global", label: "Global", short: "Global" },
  { id: "region-west", label: "West Africa", short: "West" },
  { id: "region-east", label: "East Africa", short: "East" },
  { id: "region-north", label: "North Africa", short: "North" },
  { id: "region-southern", label: "Southern Africa", short: "South" },
];

export default function Chat() {
  const { user } = useAuth();
  const [room, setRoom] = useState(() => defaultRoomFor(user?.country));
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [online, setOnline] = useState(0);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);
  const wsRef = useRef(null);

  const isAdmin = user?.role === "super_admin" || user?.role === "sub_admin" || user?.is_admin;

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }
    });
  }, []);

  // Load messages whenever the active room changes
  useEffect(() => {
    let alive = true;
    setMessages([]); setPinned(null);
    api.get("/chat/messages", { params: { room } })
      .then(({ data }) => {
        if (!alive) return;
        setMessages(data.messages || []);
        setPinned(data.pinned || null);
        setOnline(data.online || 0);
        scrollToEnd();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load chat"));
    return () => { alive = false; };
  }, [room, scrollToEnd]);

  // WebSocket — reconnect on room change
  useEffect(() => {
    const token = localStorage.getItem("nk_session_token");
    if (!token) return;
    const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/chat/ws?token=${encodeURIComponent(token)}&room=${room}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data);
        if (evt.type === "message") {
          setMessages((m) => [...m.slice(-199), evt.message]);
          scrollToEnd();
        } else if (evt.type === "delete") {
          setMessages((m) => m.map((x) => x.message_id === evt.message_id ? { ...x, deleted: true } : x));
          setPinned((p) => (p?.message_id === evt.message_id ? null : p));
        } else if (evt.type === "pin") {
          setPinned(evt.message);
        } else if (evt.type === "unpin") {
          setPinned((p) => (p?.message_id === evt.message_id ? null : p));
        } else if (evt.type === "online") {
          setOnline(evt.count);
        }
      } catch { /* ignore */ }
    };
    ws.onerror = () => {};
    return () => { try { ws.close(); } catch { /* ignore */ } };
  }, [room, scrollToEnd]);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post("/chat/send", { body, room });
      setDraft("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const onDelete = async (m) => {
    if (!confirm("Delete this message for everyone?")) return;
    try {
      await api.post(`/chat/messages/${m.message_id}/delete`, { reason: "Removed by moderator" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const onPin = async (m) => {
    try {
      await api.post(`/chat/messages/${m.message_id}/pin`);
      toast.success("Pinned");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pin failed");
    }
  };

  const onUnpin = async (m) => {
    try {
      await api.post(`/chat/messages/${m.message_id}/unpin`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unpin failed");
    }
  };

  const activeRoom = ROOMS.find((r) => r.id === room) || ROOMS[0];

  return (
    <div className="space-y-4" data-testid="chat-page">
      <header>
        <p className="eyebrow mb-1 text-white/55">Lobby</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white flex items-center gap-3">
          <Globe size={28} weight="duotone" className="text-nk-gold" />
          Chat
        </h1>
        <p className="text-sm text-white/60 mt-1">One room for the league. Pick global or your region.</p>
      </header>

      {/* Room tabs — horizontally scrollable on mobile */}
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar -mx-1 px-1" role="tablist" data-testid="chat-room-tabs">
        {ROOMS.map((r) => (
          <button
            key={r.id}
            role="tab"
            aria-selected={room === r.id}
            data-testid={`chat-room-${r.id}`}
            onClick={() => setRoom(r.id)}
            className={`shrink-0 px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all border ${
              room === r.id
                ? "bg-nk-green text-white border-nk-gold"
                : "border-white/10 text-white/60 hover:text-white hover:border-white/20"
            }`}
          >
            <span className="sm:hidden">{r.short}</span>
            <span className="hidden sm:inline">{r.label}</span>
          </button>
        ))}
        <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-white/55 shrink-0">
          <Circle size={8} weight="fill" className="text-nk-good animate-pulse" />
          <span className="font-mono" data-testid="chat-online-count">{online}</span> here
        </span>
      </div>

      {pinned && !pinned.deleted && (
        <div className="nk-card p-3 border-l-2 border-nk-gold flex items-start gap-3" data-testid="chat-pinned">
          <Pushpin size={14} weight="fill" className="text-nk-gold mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[11px] text-white/55 uppercase tracking-wider mb-0.5">
              <span>Pinned</span>
              <span>·</span>
              <span className="text-nk-gold">{pinned.name}</span>
            </div>
            <p className="text-sm text-white break-words">{pinned.body}</p>
          </div>
          {isAdmin && (
            <button onClick={() => onUnpin(pinned)} className="text-white/55 hover:text-white text-xs shrink-0">
              Unpin
            </button>
          )}
        </div>
      )}

      <div ref={scrollerRef}
           className="nk-card p-4 h-[60vh] overflow-y-auto space-y-2"
           data-testid="chat-scroller">
        {messages.length === 0 && (
          <p className="text-sm text-white/45 text-center mt-12">
            No messages yet in <span className="text-nk-gold">{activeRoom.label}</span> — break the silence.
          </p>
        )}
        {messages.map((m) => (
          <MessageRow key={m.message_id} m={m} isAdmin={isAdmin}
                      isOwn={m.user_id === user?.user_id}
                      onDelete={onDelete} onPin={onPin} />
        ))}
      </div>

      <form onSubmit={send} className="flex items-center gap-2" data-testid="chat-form">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
          placeholder={`Say something in ${activeRoom.label}...`}
          maxLength={MAX_LEN}
          disabled={sending}
          data-testid="chat-input"
          className="flex-1 bg-black/40 border border-white/10 px-4 py-2.5 text-sm rounded-sm focus:border-nk-gold outline-none transition-colors disabled:opacity-40 text-white"
        />
        <span className={`text-[11px] font-mono w-10 text-right ${draft.length > MAX_LEN - 30 ? "text-nk-bad" : "text-white/40"}`}>
          {MAX_LEN - draft.length}
        </span>
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          data-testid="chat-send-btn"
          className="nk-btn nk-btn-primary disabled:opacity-40"
        >
          <PaperPlaneRight size={14} weight="bold" /> Send
        </button>
      </form>
    </div>
  );
}

function MessageRow({ m, isAdmin, isOwn, onDelete, onPin }) {
  if (m.deleted) {
    return (
      <div className="text-xs text-white/30 italic px-2 py-1">
        — message deleted —
      </div>
    );
  }
  return (
    <div className={`group flex items-start gap-2 px-2 py-1.5 rounded-sm hover:bg-white/5 ${isOwn ? "bg-nk-gold/5" : ""}`}>
      <div className="shrink-0 mt-0.5">
        {m.country ? <CountryFlag country={m.country} size={16} /> : <span className="inline-block w-4 h-4 rounded-sm bg-white/10" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`text-xs font-medium ${m.is_admin ? "text-nk-gold" : "text-white"}`}>
            {m.name}
            {m.is_admin && <ShieldCheck size={10} weight="fill" className="inline ml-1 -translate-y-px" />}
          </span>
          <span className="text-[10px] text-white/35 font-mono">{formatTime(m.created_at)}</span>
        </div>
        <p className="text-sm text-white/95 break-words mt-0.5 whitespace-pre-wrap">{m.body}</p>
      </div>
      {isAdmin && (
        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity shrink-0">
          <button onClick={() => onPin(m)} className="p-1 text-white/45 hover:text-nk-gold" title="Pin"
                  data-testid={`chat-pin-${m.message_id}`}>
            <Pushpin size={12} />
          </button>
          <button onClick={() => onDelete(m)} className="p-1 text-white/45 hover:text-nk-bad" title="Delete"
                  data-testid={`chat-delete-${m.message_id}`}>
            <Trash size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
